import { Icon } from "@iconify/react/dist/iconify.js";
import { ActionIcon, Drawer, Flex, ScrollArea, Text } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useFetcher } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import {
  GetAllNotificationQuery,
  Notification,
} from "~/graphql/__generated__/types";
import { formatDate } from "~/lib/date";
import useFetchData from "~/lib/fetcher/use-fetch-data";
import { mergeArrays } from "~/lib/options/merge-array";
import { loader } from "~/routes/data/notification/_index/route";

import InputSearch from "../inputs/input-search";

import { schema } from "./constants";
import EmptyNotification from "./empty-notification";
import NotificationFilterForm from "./notification-filter-form";
import NotificationItem from "./notification-item";
import NotificationLoading from "./notification-loading";
import NotificationTransactionItem from "./notification-transaction";

const NotificationDrawer = ({
  open,
  close,
}: {
  open: boolean;
  close: () => void;
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const form = useForm<Partial<z.infer<typeof schema>>>({
    initialValues: {
      start_date: undefined,
      end_date: undefined,
    },
    validate: zodResolver(schema),
  });

  const [search, setSearch] = useState("");
  const {
    data,
    loading: loadingNotifications,
    setReloadAgain,
    setParams,
  } = useFetchData<typeof loader, GetAllNotificationQuery["notificationMany"]>({
    urlPath: "/data/notification",
    nestedKeys: "data.notificationMany",
  });

  useEffect(() => {
    if (open) {
      setReloadAgain(true);
    }
  }, [open]);

  const onSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearch(e.currentTarget.value);
      setParams({ s: e.currentTarget.value });
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  function handleScroll(position: { x: number; y: number }) {
    if (!scrollRef.current) {
      return;
    }
    const { y } = position;

    const container = scrollRef.current;
    const scrollPos = y + container.clientHeight;
    const scrollHeight = container.scrollHeight;
    if (Math.ceil(scrollPos) >= scrollHeight && data.pageInfo?.hasNextPage) {
      setParams((p) => ({ ...p, page: p.page + 1 }));
      setReloadAgain(true);
    }
  }

  const [notificationsData, setNotificationsData] = useState<
    GetAllNotificationQuery["notificationMany"]["nodes"]
  >([]);

  useEffect(() => {
    if (!data?.nodes || !data?.totalCount) {
      return;
    }
    if (data?.nodes && notificationsData.length < data?.totalCount) {
      setNotificationsData((prev) => mergeArrays(prev, data.nodes, "id"));
    }
  }, [data?.nodes]);

  const onApplyFilter = (values: z.infer<typeof schema>) => {
    setParams({
      start_date: formatDate(values.start_date, "YYYY-MM-DD"),
      end_date: formatDate(values.end_date, "YYYY-MM-DD"),
    });
    setIsFilterOpen(false);
    setNotificationsData([]);
    setReloadAgain(true);
  };
  function onCloseDrawer() {
    setSearch("");
    form.reset();
    close();
  }

  const fetcher = useFetcher();
  useEffect(() => {
    if (data && data?.nodes?.length != 0) {
      const isAnyUnreadNotification = data?.nodes?.some(
        (e) => e.readAt === null,
      );
      if (isAnyUnreadNotification) {
        fetcher.submit(
          {},
          {
            method: "POST",
            action: "/data/notification/read-all",
          },
        );
      }
    }
  }, [data?.nodes?.length]);

  return (
    <Drawer
      opened={open}
      onClose={onCloseDrawer}
      title={
        <Text size="xl" fw={600}>
          Notification
        </Text>
      }
      position="right"
      closeButtonProps={{
        icon: <Icon icon="tabler:x" className="text-red-500 h-5 w-5" />,
      }}
      size="sm"
      classNames={{
        body: "h-full",
        content: "overflow-hidden",
      }}
    >
      <Flex gap={8}>
        <InputSearch
          value={search}
          className="flex-1"
          onChange={(e) => setSearch(e.currentTarget.value)}
          onKeyUp={onSearch}
        />

        <ActionIcon
          variant={isFilterOpen ? "filled" : "outline"}
          size="32"
          color={isFilterOpen ? "tmGreen" : "gray.5"}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <Icon
            icon="tabler:filter"
            className="h-4 w-4 text-[var(--mantine-color-gray)r]"
          />
        </ActionIcon>
      </Flex>

      {isFilterOpen ? (
        <NotificationFilterForm
          form={form}
          setIsFilterOpen={setIsFilterOpen}
          onApplyFilter={onApplyFilter}
        />
      ) : (
        <ScrollArea
          className="h-[calc(100vh-112px)] mb-6"
          type="never"
          viewportRef={scrollRef}
          onScrollPositionChange={handleScroll}
        >
          {(!notificationsData || notificationsData.length === 0) &&
          !loadingNotifications ? (
            <EmptyNotification />
          ) : (
            notificationsData.map((item) =>
              item.type === "REMINDER" && item.content && !item.content.data ? (
                <NotificationItem
                  key={item?.id}
                  date={item?.date}
                  {...item?.content}
                />
              ) : (
                <>
                  <NotificationTransactionItem
                    key={item?.id}
                    close={onCloseDrawer}
                    {...(item as Notification)}
                  />
                </>
              ),
            )
          )}
          {loadingNotifications
            ? Array.from({ length: 7 }, (_, e) => e).map((e, idx) => (
                <NotificationLoading key={idx} id={e} />
              ))
            : null}
        </ScrollArea>
      )}
    </Drawer>
  );
};

export default NotificationDrawer;
